/** TECHNOLOGY → PRODUCT CONTENT — coverage, data quality, bulk enrich, compare. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BookOpenCheck, RefreshCw, Play, GitCompareArrows, Search } from "lucide-react";
import { VtigerTable } from "@/components/app/VtigerTable";

const TABS = ["Brand Coverage", "Data Quality", "Description Proposals", "Bulk Enrichment", "Compare Products"];

function DescriptionProposals() {
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/pc/description-proposals?limit=500")
    .then(({ data }) => { setRows(data.items || []); setChecked([]); }), []);
  useEffect(() => { load(); }, [load]);
  const approve = async () => {
    if (!checked.length) return toast.info("Select products first");
    setBusy(true);
    try {
      const { data } = await api.post("/pc/description-proposals/approve", { product_ids: checked });
      toast.success(`${data.applied} description(s) approved & applied`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="pc-description-proposals">
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{rows.length} products missing a description have Icecat content available — select and approve.</span>
        <button onClick={approve} disabled={busy || checked.length === 0} data-testid="approve-descriptions-btn"
          className="ml-auto h-9 px-4 text-sm bg-emerald-700 text-white rounded disabled:opacity-40">
          Approve Selected ({checked.length})</button>
      </div>
      <VtigerTable testid="description-proposals-table" rows={rows} pageSize={25}
        onCheckedChange={setChecked}
        columns={[
          { key: "product_name", label: "Product", cls: "font-medium" },
          { key: "brand", label: "Brand", width: 100 },
          { key: "oem_part_number", label: "MPN", mono: true, width: 130 },
          { key: "proposed", label: "Proposed description",
            render: (r) => <span className="block max-w-[520px] text-slate-600" title={r.proposed}>{r.proposed.slice(0, 180)}{r.proposed.length > 180 ? "…" : ""}</span> },
          { key: "provider", label: "Source", width: 110, mono: true },
        ]} />
    </div>
  );
}

function BrandCoverage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/pc/brand-coverage").then(({ data }) => setRows(data.items || [])); }, []);
  return (
    <VtigerTable testid="brand-coverage-table" rows={rows.map((r) => ({ ...r, id: r.brand }))}
      pageSize={25} viewKey="brand-coverage"
      columns={[
        { key: "brand", label: "Brand", cls: "font-semibold" },
        { key: "products", label: "ONE Products", align: "right", num: true },
        { key: "checked", label: "Checked", align: "right", num: true },
        { key: "exact_matches", label: "Exact Matches", align: "right", num: true },
        { key: "with_specifications", label: "With Specs", align: "right", num: true },
        { key: "with_datasheets", label: "With Datasheets", align: "right", num: true },
        { key: "no_match", label: "No Match", align: "right", num: true },
        { key: "coverage_pct", label: "Coverage %", align: "right", num: true,
          render: (r) => <span className={r.coverage_pct > 50 ? "text-emerald-700 font-semibold" : ""}>{r.coverage_pct}%</span> },
      ]} />
  );
}

function DataQuality() {
  const [dq, setDq] = useState(null);
  useEffect(() => { api.get("/pc/data-quality").then(({ data }) => setDq(data)); }, []);
  if (!dq) return <div className="text-xs text-slate-400 p-4">Loading data quality…</div>;
  const CARDS = [
    ["missing_description", "Missing Description"], ["missing_datasheet", "Missing Datasheet"],
    ["missing_image", "Missing Image"], ["missing_brand", "Missing Brand"],
    ["missing_mpn", "Missing MPN"], ["no_content_match", "No Content Match"],
    ["multiple_match", "Multiple Match"], ["country_mismatch", "Country Mismatch"],
    ["outdated_content", "Outdated Content"], ["review_required", "Review Required"],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pc-data-quality">
      {CARDS.map(([k, label]) => (
        <div key={k} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`dq-${k}`}>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold text-slate-700 flex-1">{label}</span>
            <span className={`text-lg font-bold ${dq[k]?.count > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              {(dq[k]?.count ?? 0).toLocaleString()}</span>
          </div>
          {(dq[k]?.sample || []).slice(0, 4).map((s) => (
            <div key={s.id || s.product_id} className="text-[10px] text-slate-500 truncate mt-1">
              {s.product_name || s.product_id} {s.brand ? `· ${s.brand}` : ""}</div>))}
        </div>))}
    </div>
  );
}

function BulkEnrich() {
  const [form, setForm] = useState({ brand: "", missing_content: true, limit: 100 });
  const [status, setStatus] = useState(null);
  const load = useCallback(() => api.get("/pc/bulk-enrich/status").then(({ data }) => setStatus(data)), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  const run = async () => {
    try {
      await api.post("/pc/bulk-enrich", { ...form, brand: form.brand || undefined });
      toast.success("Bulk enrichment started"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
  };
  const st = status?.latest;
  return (
    <div className="space-y-3" data-testid="pc-bulk-enrich">
      <div className="bg-white border border-slate-200 rounded-md p-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[10px] uppercase text-slate-500 block">Brand (optional)</label>
          <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
            data-testid="bulk-brand-input" className="h-9 px-2 border border-slate-300 rounded text-sm w-48" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-slate-500 block">Limit</label>
          <input type="number" value={form.limit} onChange={(e) => setForm({ ...form, limit: Number(e.target.value) })}
            className="h-9 px-2 border border-slate-300 rounded text-sm w-24" />
        </div>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 h-9">
          <input type="checkbox" checked={form.missing_content}
            onChange={(e) => setForm({ ...form, missing_content: e.target.checked })} />
          Only products without content</label>
        <button onClick={run} disabled={status?.running} data-testid="bulk-run-btn"
          className="h-9 px-4 text-sm bg-emerald-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          <Play className="h-3.5 w-3.5" /> {status?.running ? "Running…" : "Enrich Products"}</button>
      </div>
      {st && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="bulk-status-card">
          <div className="text-[11px] text-slate-500 mb-2">
            Last run {new Date(st.run_at).toLocaleString()} · status{" "}
            <span className={`font-semibold ${st.status === "PASS" ? "text-emerald-700" :
              st.status === "SETUP_REQUIRED" ? "text-amber-700" : "text-slate-700"}`}>{st.status}</span>
            {st.status === "SETUP_REQUIRED" && " — configure a provider (e.g. Open Icecat) in ONE Connect → Product Content"}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
            {Object.entries(st.stats || {}).map(([k, v]) => (
              <div key={k} className="border border-slate-100 rounded p-2">
                <div className="text-lg font-bold">{v}</div>
                <div className="text-[9px] uppercase text-slate-500">{k.replace(/_/g, " ")}</div>
              </div>))}
          </div>
        </div>)}
    </div>
  );
}

function CompareProducts() {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) return setHits([]);
    const t = setTimeout(() => api.get(`/product-master/products?q=${encodeURIComponent(q)}&page_size=6`)
      .then(({ data }) => setHits(data.items || [])), 300);
    return () => clearTimeout(t);
  }, [q]);
  const compare = async () => {
    try {
      const { data } = await api.post("/pc/compare", { product_ids: selected.map((s) => s.id) });
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Compare failed"); }
  };
  return (
    <div className="space-y-3" data-testid="pc-compare">
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
        <div className="relative w-96">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products to compare…"
            data-testid="compare-search" className="h-8 pl-7 pr-2 border border-slate-300 rounded text-sm w-full" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {hits.map((h) => (
            <button key={h.id} onClick={() => selected.length < 5 && !selected.find((s) => s.id === h.id) &&
              setSelected([...selected, h])}
              className="text-[10px] px-2 py-1 rounded border border-slate-300 hover:bg-emerald-50">{h.product_name}</button>))}
        </div>
        <div className="flex gap-1.5 flex-wrap items-center">
          {selected.map((s) => (
            <span key={s.id} className="text-[10px] px-2 py-1 rounded-full bg-emerald-700 text-white cursor-pointer"
              onClick={() => setSelected(selected.filter((x) => x.id !== s.id))}>{s.product_name} ✕</span>))}
          <button onClick={compare} disabled={selected.length < 2} data-testid="compare-run-btn"
            className="h-8 px-3 text-xs bg-slate-900 text-white rounded inline-flex items-center gap-1 disabled:opacity-40">
            <GitCompareArrows className="h-3 w-3" /> Compare ({selected.length}/5)</button>
        </div>
      </div>
      {result && (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="compare-result">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-2 py-2 font-semibold">Attribute</th>
                {result.products.map((p) => <th key={p.id} className="px-2 py-2 font-semibold">{p.product_name}</th>)}
                <th className="px-2 py-2 font-semibold w-24">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {result.attributes.length === 0 && (
                <tr><td colSpan={result.products.length + 2} className="p-6 text-center text-slate-400">
                  No technical attributes available yet — run Find Product Information on these products first.</td></tr>)}
              {result.attributes.map((a) => (
                <tr key={a.attribute} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 font-medium text-slate-700">{a.attribute}</td>
                  {a.values.map((v, i) => <td key={i} className="px-2 py-1.5">{v ?? "—"}</td>)}
                  <td className="px-2 py-1.5">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                      a.verdict === "SAME" ? "bg-emerald-100 text-emerald-800" :
                      a.verdict === "DIFFERENT" ? "bg-amber-100 text-amber-800" :
                      "bg-slate-100 text-slate-500"}`}>{a.verdict.replace("_", " ")}</span></td>
                </tr>))}
            </tbody>
          </table>
        </div>)}
    </div>
  );
}

export default function ProductContentHub() {
  const [tab, setTab] = useState(TABS[0]);
  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pc-hub-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center"><BookOpenCheck className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">ONE Product Content</h1>
          <p className="text-xs text-slate-500">Brand coverage, content data quality, bulk enrichment and product comparison</p>
        </div>
        <a href="/admin/one-connect/product-content"
          className="h-9 px-3 text-sm border border-slate-300 rounded inline-flex items-center"
          data-testid="pc-hub-providers-link">Providers & Sources →</a>
      </header>
      <div className="flex gap-1.5 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`pc-tab-${t.toLowerCase().replace(/\W+/g, "-")}`}
            className={`h-8 px-3 text-xs font-medium rounded-full border ${tab === t
              ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-slate-600 border-slate-300"}`}>{t}</button>))}
      </div>
      {tab === "Brand Coverage" && <BrandCoverage />}
      {tab === "Data Quality" && <DataQuality />}
      {tab === "Description Proposals" && <DescriptionProposals />}
      {tab === "Bulk Enrichment" && <BulkEnrich />}
      {tab === "Compare Products" && <CompareProducts />}
    </div>
  );
}
